import {
  NO_SOUND_THRESHOLD_DISTANCE,
  NOT_DISTANCE_SENSITIVE_SOUND_THRESHOLD_DISTANCE,
  ROOM_FULL_SPACE_HEIGHT,
  ROOM_FULL_SPACE_WIDTH,
} from '@/utils/constants';
import { CHOSEN_DEFAULT_DATA_SERVER } from '@/config/server';
import { SPACE_ROOM_SEPARATOR } from '@/utils/constants';

import {
  BoundingPosition,
  CoordinatesPosition,
  DropdownOption,
  Room,
  RoomPosition,
  RoomSpace,
  SubRoom,
  zawaroom,
  User,
  UploadFileType,
} from '@/utils/models';
import store from '@/store/vuex/index';
import { DataService } from '@/service/DataService';
import axios from 'axios';
import dayjs from 'dayjs';

export function isGuest(user: User, space: RoomSpace) {
  if (user.email) {
    const email = user.email.toLocaleLowerCase();
    const domain = email.substring(email.indexOf('@'));
    if (user.authType === 'guest' || !space.organizations.find((o) => domain.endsWith(o))) {
      return true;
    }
  } else {
    return true;
  }
  return false;
}

export function highlightElement(element: HTMLElement | undefined): void {
  if (element) {
    element.classList.add('spatial-chat__video--pulse');
    setTimeout(function () {
      element.classList.remove('spatial-chat__video--pulse');
    }, 2000);
  }
}

function isInRoom(subRoomPosition: SubRoom, position: CoordinatesPosition): boolean {
  let offset = { x: 116 / 2, y: 116 / 2 }; // pos は　左上なので中心にずらす
  let posx = position.x + offset.x;
  let posy = position.y + offset.y;
  return (
    posx > subRoomPosition.topLeft.x &&
    posx < subRoomPosition.bottomRight.x &&
    posy > subRoomPosition.topLeft.y &&
    posy < subRoomPosition.bottomRight.y
  );
}

export function getCurrentRoom(position: CoordinatesPosition, roomsPositionsList: Array<SubRoom>): SubRoom | undefined {
  const currentRoom = roomsPositionsList.find((room: SubRoom) => {
    return isInRoom(room, position);
  });
  return currentRoom; // -1 if not in any room, but in the free space
}

export function isInSameRoom(
  participantPosition: CoordinatesPosition,
  roomsPositionsList: Array<SubRoom>,
  localVideoRoom?: SubRoom,
): boolean {
  const participantRoom = getCurrentRoom(participantPosition, roomsPositionsList);
  return participantRoom === localVideoRoom;
}

export function isSupportedImageExtension(extension: string): boolean {
  return extension === 'image/gif' || extension === 'image/jpeg' || extension === 'image/png';
}

// export function getConnectionConfig(roomName: string, serverName: string): JitsiConnectionConfig {
//   const serverNameParsed = serverName.replace(/https?:\/\/(.*)\/?$/, "$1");
//   const httpOrHttps = serverName.startsWith("https://") ? "https" : "http";

//   const muc = `conference.${serverNameParsed}`;
//   // const serviceUrl = `${httpOrHttps}://${serverNameParsed}/http-bind?room=${roomName}`; // `wss://${serverNameParsed}/xmpp-websocket?room=${roomName}`;  // `//${serverNameParsed}/http-bind?room=${roomName}`;
//   const serviceUrl = `wss://${serverNameParsed}/xmpp-websocket?room=${roomName}`; // `//${serverNameParsed}/http-bind?room=${roomName}`;
//   const clientNode = `${httpOrHttps}://${serverNameParsed}`; // `https://${serverNameParsed}`; // 'http://jitsi.org/jitsimeet';

//   const connectionConfig: JitsiConnectionConfig = {
//     hosts: {
//       domain: serverNameParsed,
//       muc: muc,
//     },
//     serviceUrl: serviceUrl,
//     clientNode: clientNode,
//     // modules_enabled: ["presence_identity"]
//   };
//   console.log("Jitsi server connection configuration:", connectionConfig);

//   return connectionConfig;
// }

export function getDataServerUrl(): string {
  return location.origin;
}
export function getMediaServerUrl(): string {
  return store.state.siteInfo?.defaultMediaServerUrl || '';
}
function convertStringArrayToDropdownOption(stringArray: Array<string>): Array<DropdownOption> {
  return stringArray.map((str: string) => {
    return {
      id: str,
      label: str,
    };
  });
}

export function findDeviceOnDevicesList(
  devices: Array<DropdownOption>,
  deviceId: string | undefined,
): DropdownOption | undefined {
  const device = devices.find((d: DropdownOption) => {
    return d.id === deviceId;
  });
  return device;
}

export function printMediaDevicesState(
  hasWebcam: boolean,
  hasMicrophone: boolean,
  hasSpeakers: boolean,
  isMicrophoneAlreadyCaptured: boolean,
  isWebcamAlreadyCaptured: boolean,
): void {
  console.log('hasWebCam: ', hasWebcam);
  console.log('hasMicrophone: ', hasMicrophone);
  console.log('hasSpeakers: ', hasSpeakers);
  console.log('isMicrophoneAlreadyCaptured: ', isMicrophoneAlreadyCaptured);
  console.log('isWebcamAlreadyCaptured: ', isWebcamAlreadyCaptured);
}

export function getCoordinatesPositionWithoutScale(
  notScaledPosition: CoordinatesPosition,
  scale: number,
  offsetOrNull?: CoordinatesPosition,
  bounding?: BoundingPosition,
): CoordinatesPosition {
  const offset = offsetOrNull ?? { x: 0, y: 0 };
  const scaledPos = {
    x: Math.round(notScaledPosition.x / scale - offset.x),
    y: Math.round(notScaledPosition.y / scale - offset.y),
  };

  if (bounding) {
    if (scaledPos.x < bounding.x) {
      scaledPos.x = bounding.x;
    } else if (scaledPos.x > bounding.width + bounding.x) {
      scaledPos.x = bounding.width + bounding.x - offset.x * 2; /// *2 は、中心が来ていることを想定している
    }

    if (scaledPos.y < bounding.y) {
      scaledPos.y = bounding.y;
    } else if (scaledPos.y > bounding.height + bounding.y) {
      scaledPos.y = bounding.height + bounding.y - offset.y * 2; /// *2 は、中心が来ていることを想定している
    }
  }

  return scaledPos;
}

export function getViewportSize(viewportElement: HTMLDivElement | null): CoordinatesPosition {
  if (viewportElement) {
    const viewportRect = viewportElement.getBoundingClientRect();
    return {
      x: viewportRect.width,
      y: viewportRect.height,
    };
  } else {
    return {
      x: ROOM_FULL_SPACE_WIDTH,
      y: ROOM_FULL_SPACE_HEIGHT,
    };
  }
}

export function getMinScale(viewportElement: HTMLDivElement | null): number {
  if (viewportElement) {
    const viewportHeight: CoordinatesPosition = getViewportSize(viewportElement);
    return viewportHeight.y / ROOM_FULL_SPACE_HEIGHT;
  }
  return 0.3;
}

export function getMaxViewportTranslateValues(
  viewportSizeWithoutScale: CoordinatesPosition,
  roomScale: number,
): CoordinatesPosition {
  return {
    x: -(ROOM_FULL_SPACE_WIDTH - viewportSizeWithoutScale.x) * roomScale,
    y: -(ROOM_FULL_SPACE_HEIGHT - viewportSizeWithoutScale.y) * roomScale,
  };
}

export function getTranslateValuesAdjustedToViewport(
  viewportTranslateValues: CoordinatesPosition,
  viewportSize: CoordinatesPosition,
  roomScale: number,
): CoordinatesPosition {
  const viewportSizeWithoutScale = getCoordinatesPositionWithoutScale(viewportSize, roomScale);
  const MaxTranslate = getMaxViewportTranslateValues(viewportSizeWithoutScale, roomScale);

  const adjustedViewportTranslateValues = Object.assign({}, viewportTranslateValues);
  if (viewportTranslateValues.x > 0) {
    adjustedViewportTranslateValues.x = 0;
  } else if (viewportTranslateValues.x < MaxTranslate.x) {
    adjustedViewportTranslateValues.x = MaxTranslate.x;
  }

  if (viewportTranslateValues.y > 0) {
    adjustedViewportTranslateValues.y = 0;
  } else if (viewportTranslateValues.y < MaxTranslate.y) {
    adjustedViewportTranslateValues.y = MaxTranslate.y;
  }

  return adjustedViewportTranslateValues;
}

export function getTranslateViewportAfterZoom(
  previousScale: number,
  currentScale: number,
  previousViewportTranslateValues: CoordinatesPosition,
  viewportSize: CoordinatesPosition,
): CoordinatesPosition {
  const previous = {
    x: previousScale * previousViewportTranslateValues.x,
    y: previousScale * previousViewportTranslateValues.y,
  };
  const current = {
    x: currentScale * previousViewportTranslateValues.x,
    y: currentScale * previousViewportTranslateValues.y,
  };
  const difference = {
    x: previous.x - current.x,
    y: previous.y - current.y,
  };
  const newTranslateValues = {
    x: previousViewportTranslateValues.x - 2 * difference.x,
    y: previousViewportTranslateValues.y - 2 * difference.y,
  };
  const adjustedTranslateViewportValues = getTranslateValuesAdjustedToViewport(
    newTranslateValues,
    viewportSize,
    currentScale,
  );
  return adjustedTranslateViewportValues;
}

export function addErrorMessage(title: 'ルーム' | 'スペース', isAlreadyAdded: boolean, convertedName: string): string {
  if (isAlreadyAdded) {
    return `同じ${title}名が存在します。`;
  } else if (convertedName === '') {
    return `${title}名を空にすることはできません。`;
  }
  return '';
}

export function setFocusToInput(inputRef: any): void {
  if (inputRef) {
    (inputRef as HTMLInputElement).focus();
  }
}

export function removeCssClassFromHTMLElement(el: HTMLElement | null, cssClass: string): void {
  if (el) {
    el.classList.remove(cssClass);
  }
}

export function getEventMouseOrTouchXY(e: TouchEvent | MouseEvent): CoordinatesPosition {
  let xyPosition: CoordinatesPosition = {
    x: 0,
    y: 0,
  };
  if (window.TouchEvent && e instanceof TouchEvent) {
    if (e.touches && e.touches.length > 0) {
      xyPosition = {
        x: e.touches[0].clientX,
        y: e.touches[0].clientY,
      };
    }
  } else if (e instanceof MouseEvent) {
    xyPosition = {
      x: e.x,
      y: e.y,
    };
  }

  return xyPosition;
}

/**
 * タッチされた座標を返す。(複数の指でのタッチに対応)
 * @param e
 */
export function getMultiTouchXY(e: TouchEvent): CoordinatesPosition[] {
  const coordinates: CoordinatesPosition[] = [];
  for (const t of e.touches) {
    coordinates.push({
      x: t.clientX,
      y: t.clientY,
    });
  }
  return coordinates;
}

export function getCenterPoint(coordinates: CoordinatesPosition[]): CoordinatesPosition {
  const avgX = Math.floor(
    coordinates.map((c) => c.x).reduce((previous, current) => previous + current) / coordinates.length,
  );
  const avgY = Math.floor(
    coordinates.map((c) => c.y).reduce((previous, current) => previous + current) / coordinates.length,
  );
  return {
    x: avgX,
    y: avgY,
  };
}

export function getDistanceBetweenPoints(point1: CoordinatesPosition, point2: CoordinatesPosition): number {
  const dx = point1.x - point2.x;
  const dy = point1.y - point2.y;
  return Math.sqrt(Math.pow(dx, 2) + Math.pow(dy, 2));
}

// required for touch screens on elements that can be moved on touch
// if it's not added - the screen will move along with the touched element
export function setTouchActionToNone(domElement: HTMLElement | null): void {
  if (domElement) {
    domElement.style.touchAction = 'none';
  }
}

export function isIPad(): boolean {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
}

export async function uploadFile(dataService: DataService, fileType: UploadFileType, file: File): Promise<string> {
  const req = await dataService.emitRequestUpload(fileType);
  const data = new FormData();
  data.append('file', file);
  data.append('req', JSON.stringify(req));
  const res = await axios.post(`${getDataServerUrl()}/zawa-data/api/upload`, data);
  return res.data.url;
}

const base32 = 'abcdefghijklmnopqrstuv0123456789';
export const getRandomString = (n: number): string => {
  return Array.from(crypto.getRandomValues(new Uint8Array(n)))
    .map((v) => base32[v % base32.length])
    .join('');
};

export function sleepAsync(mills: number) {
  if (mills < 0) {
    return Promise.reject();
  }
  return new Promise<void>((resolv) => {
    setTimeout(() => {
      resolv();
    }, mills);
  });
}

/**
 * 管理者もしくはサイト管理者かどうか
 * @param user
 * @returns
 */
export function isAdmin(user: User) {
  return !!user.roles?.find((r) => r.id === 'Admin' || r.id === 'SitesAdmin');
}

/**
 * 管理者もしくはサイト管理はもしくはルーム管理者かどうか
 * @param user
 * @param room
 * @returns
 */
export function isAdminOrRoomAdmin(user: User, room: Room) {
  return isAdmin(user) || isRoomAdmin(user, room);
}

/**
 * 管理者かどうか
 * @param user
 * @returns
 */
export function isAdministrator(user: User) {
  return !!user.roles?.find((r) => r.id === 'Admin');
}
/**
 * サイト管理者かどうか
 * @param user
 * @returns
 */
export function isSitesAdmin(user: User) {
  return !!user.roles?.find((r) => r.id === 'SitesAdmin');
}
/**
 * ルーム管理者かどうか
 * @param user
 * @param room
 * @returns
 */
export function isRoomAdmin(user: User, room: Room) {
  return !!user.email && !!room.adminEmails?.find((e) => e === user.email);
}

export function removeProps<T, KeyT extends keyof T>(target: T, ...props: KeyT[]) {
  if (!target || !props?.length) {
    return target;
  }

  props.forEach((p) => delete target[p]);
  return target;
}

export function formatTime(date: Date | number): string {
  if (!date) return '';
  if (typeof date === 'number' || date instanceof Date) {
    return dayjs(date).format('HH:mm');
  }
  return '';
}
